import { useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { UNF_INVOICE_UPLOAD_ROLES, type UnfInvoiceReviewDto } from '@crm/shared'
import { useAuthStore } from '../../auth/authStore'
import { dismissUnfInvoiceReview, listUnfInvoiceReviews } from '../../../shared/api/orders.api'
import { apiErrorText } from '../../../shared/api/errorText'
import { confirm } from '../../../shared/ui/confirmStore'
import { toast } from '../../../shared/ui/toastStore'
import { PageLoader } from '../../../shared/ui/Spinner'
import { ErrorMessage } from '../../../shared/ui/ErrorMessage'
import { EmptyState } from '../../../shared/ui/EmptyState'
import { UnfInvoiceModal } from './UnfInvoiceModal'

export const UNF_REVIEWS_KEY = ['unf-invoice-reviews'] as const

const ruDate = (iso: string | null): string =>
  iso ? iso.slice(0, 10).split('-').reverse().join('.') : '—'
const rub = (v: number | null): string =>
  v == null ? '—' : Math.round(v).toLocaleString('ru-RU') + ' ₽'

/**
 * «Счета на проверке» (docs/features/unf-invoice-upload.md, 16.10.2026): счета из 1С,
 * которые отложили, чтобы разобрать вручную (с 21.10.2026 заказ сам подстраивается под
 * счёт, сюда попадают, например, счета к оплаченному заказу с расхождением). «Проверить ещё раз» сверяет
 * сохранённый счёт заново — файл грузить не нужно. Бухгалтер и администратор видят
 * все отложенные счета, менеджер, РОП и помощник — отложенные ими.
 */
export function UnfInvoiceReviewsButton() {
  const role = useAuthStore((s) => s.user?.role)
  const allowed = !!role && (UNF_INVOICE_UPLOAD_ROLES as string[]).includes(role)
  const [open, setOpen] = useState(false)
  const query = useQuery({
    queryKey: UNF_REVIEWS_KEY,
    queryFn: listUnfInvoiceReviews,
    enabled: allowed,
  })
  if (!allowed) return null
  const count = query.data?.length ?? 0
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`rounded-lg border px-3 py-1.5 text-sm ${
          count > 0
            ? 'border-amber-300 bg-amber-50 text-amber-800 hover:border-amber-500'
            : 'border-gray-300 bg-white text-gray-700 hover:border-brand-500'
        }`}
      >
        Счета на проверке{count > 0 ? ` · ${count}` : ''}
      </button>
      {open && <ReviewsModal onClose={() => setOpen(false)} />}
    </>
  )
}

function ReviewsModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const query = useQuery({ queryKey: UNF_REVIEWS_KEY, queryFn: listUnfInvoiceReviews })
  const [recheck, setRecheck] = useState<UnfInvoiceReviewDto | null>(null)
  const refresh = () => void qc.invalidateQueries({ queryKey: UNF_REVIEWS_KEY })

  const dismiss = async (r: UnfInvoiceReviewDto) => {
    const ok = await confirm({
      title: `Убрать счёт № ${r.invoiceNumber} из списка?`,
      message: 'В заказы он не запишется. Если понадобится, его можно загрузить из файла снова.',
      confirmText: 'Убрать',
    })
    if (!ok) return
    try {
      await dismissUnfInvoiceReview(r.id)
      refresh()
    } catch (e) {
      toast.error(apiErrorText(e, 'Не удалось убрать счёт'))
    }
  }

  const rows = query.data ?? []
  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        role="dialog"
        aria-label="Счета на проверке"
        className="flex max-h-[90vh] w-full max-w-5xl flex-col rounded-xl border border-gray-200 bg-white shadow-xl"
      >
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <h3 className="text-sm font-semibold text-gray-800">Счета из 1С на проверке</h3>
          <button
            onClick={onClose}
            aria-label="Закрыть"
            className="text-lg leading-none text-gray-400 hover:text-gray-600"
          >
            ×
          </button>
        </div>
        <div className="space-y-3 overflow-y-auto p-4">
          {query.isLoading ? (
            <PageLoader label="Загружаю счета…" />
          ) : query.isError ? (
            <ErrorMessage error={query.error} />
          ) : rows.length === 0 ? (
            <EmptyState
              title="Счетов на проверке нет"
              description="Сюда попадают счета из 1С, которые отложили, чтобы разобрать вручную."
            />
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="text-left text-xs uppercase text-gray-500">
                    <tr>
                      <th className="py-1 pr-3">Счёт 1С</th>
                      <th className="py-1 pr-3">Клиент</th>
                      <th className="py-1 pr-3 text-right">Сумма</th>
                      <th className="py-1 pr-3">Почему на проверке</th>
                      <th className="py-1 pr-3">Отложил</th>
                      <th className="py-1" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {rows.map((r) => (
                      <tr key={r.id}>
                        <td className="whitespace-nowrap py-2 pr-3">
                          № {r.invoiceNumber} от {ruDate(r.invoiceDate)}
                        </td>
                        <td className="py-2 pr-3">{r.buyerName}</td>
                        <td className="whitespace-nowrap py-2 pr-3 text-right tabular-nums">
                          {rub(r.payable)}
                        </td>
                        <td className="py-2 pr-3 text-amber-800">{r.reason}</td>
                        <td className="whitespace-nowrap py-2 pr-3 text-gray-600">
                          {r.createdByName ?? '—'}, {ruDate(r.createdAt)}
                        </td>
                        <td className="whitespace-nowrap py-2 text-right">
                          <button
                            type="button"
                            onClick={() => setRecheck(r)}
                            className="text-brand-700 hover:underline"
                          >
                            Проверить ещё раз
                          </button>
                          <span className="px-1 text-gray-300">·</span>
                          <button
                            type="button"
                            onClick={() => void dismiss(r)}
                            className="text-gray-500 hover:underline"
                          >
                            Убрать
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-sm text-gray-500">
                «Проверить ещё раз» заново сверяет сохранённый счёт с заявками. Записанный в заказ
                счёт уходит из списка сам.
              </p>
            </>
          )}
        </div>
      </div>
      {recheck && (
        <UnfInvoiceModal
          initialInvoice={recheck.invoice}
          onClose={() => setRecheck(null)}
          onDeferred={refresh}
          onDone={(res) => {
            setRecheck(null)
            refresh()
            void qc.invalidateQueries({ queryKey: ['orders'] })
            void navigate(`/orders/${res.orderId}`)
          }}
        />
      )}
    </div>,
    document.body,
  )
}
